import { useState, type ReactNode } from "react";
import { Box, Check, Inbox } from "lucide-react";
import type { Destination, Space } from "@desk/contracts";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ProjectGlyph } from "@/components/icons";
import { useHierarchy, type Hierarchy } from "@/data/hooks";
import { cn } from "@/lib/utils";
import { SpaceIcon } from "@/components/container-icon";

export interface Dest {
  areaId: string | null;
  spaceId: string | null;
  projectId: string | null;
}

export const INBOX: Dest = { areaId: null, spaceId: null, projectId: null };

export function normalizeDest(d: Destination | null | undefined, h: Hierarchy): Dest {
  if (!d) return INBOX;
  const p = h.project(d.projectId);
  if (p) return { areaId: p.areaId, spaceId: p.spaceId, projectId: p.id };
  const s = h.space(d.spaceId);
  if (s) return { areaId: s.areaId, spaceId: s.id, projectId: null };
  const a = h.area(d.areaId);
  if (a) return { areaId: a.id, spaceId: null, projectId: null };
  return INBOX;
}

/**
 * Like normalizeDest, but a saved destination whose own list is gone (archived or deleted) becomes
 * Inbox rather than its parent, so a remembered default never files somewhere unexpected.
 */
export function savedDest(d: Destination | null | undefined, h: Hierarchy): Dest {
  if (!d) return INBOX;
  const n = normalizeDest(d, h);
  return (n.projectId ?? n.spaceId ?? n.areaId) === (d.projectId ?? d.spaceId ?? d.areaId ?? null) ? n : INBOX;
}

export function destLabel(d: Dest, h: Hierarchy): string {
  const p = h.project(d.projectId);
  if (p) return p.name;
  const s = h.space(d.spaceId);
  if (s) return s.name;
  const a = h.area(d.areaId);
  return a ? a.name : "Inbox";
}

export function destPath(d: Dest, h: Hierarchy): string {
  const parts = [h.area(d.areaId)?.name, h.space(d.spaceId)?.name, h.project(d.projectId)?.name].filter(Boolean);
  return parts.length ? parts.join(" › ") : "Inbox";
}

export function sameDest(a: Dest, b: Dest) {
  return a.areaId === b.areaId && a.spaceId === b.spaceId && a.projectId === b.projectId;
}

/** Searchable list of Inbox, areas, spaces, projects. Selecting a project sets space + area. */
export function DestinationCommand({ value, onSelect, autoFocus = true }: { value: Dest; onSelect: (d: Dest) => void; autoFocus?: boolean }) {
  const h = useHierarchy();
  const item = (key: string, d: Dest, label: ReactNode, keywords: string[], icon: ReactNode, sub?: string) => (
    <CommandItem key={key} value={key} keywords={keywords} onSelect={() => onSelect(d)}>
      {icon}
      <span className="truncate">{label}</span>
      {sub && <span className="truncate text-[12px] text-subtle">{sub}</span>}
      {sameDest(value, d) && <Check className="ml-auto !text-foreground" />}
    </CommandItem>
  );
  /** A space and its open projects; `a` is null for a top-level space. */
  const spaceItems = (s: Space, a: { id: string; name: string } | null) => [
    item(`space:${s.id}`, { areaId: a?.id ?? null, spaceId: s.id, projectId: null }, s.name, [s.name, s.prefix, a?.name ?? ""], <SpaceIcon space={s} className="size-4 text-[9px]" />),
    ...h
      .projectsOf(s.id)
      .filter((p) => p.state !== "completed")
      .map((p) =>
        item(
          `project:${p.id}`,
          { areaId: a?.id ?? null, spaceId: s.id, projectId: p.id },
          p.name,
          [p.name, s.name, a?.name ?? ""],
          <ProjectGlyph state={p.state} className="ml-1" />,
          s.name,
        ),
      ),
  ];
  return (
    <Command loop>
      <CommandInput placeholder="Move to…" autoFocus={autoFocus} />
      <CommandList>
        <CommandEmpty>No matching destination.</CommandEmpty>
        <CommandGroup>{item("inbox", INBOX, "Inbox", ["inbox"], <Inbox />)}</CommandGroup>
        {h.topSpaces.length > 0 && <CommandGroup heading="Spaces">{h.topSpaces.map((s) => spaceItems(s, null))}</CommandGroup>}
        {h.areas.map((a) => (
          <CommandGroup key={a.id} heading={a.name}>
            {item(`area:${a.id}`, { areaId: a.id, spaceId: null, projectId: null }, a.name, [a.name, a.prefix], <Box />)}
            {h.spacesOf(a.id).map((s) => spaceItems(s, a))}
          </CommandGroup>
        ))}
      </CommandList>
    </Command>
  );
}

/** Chip-style trigger + popover. */
export function DestinationPicker({
  value,
  onChange,
  children,
  align = "start",
}: {
  value: Dest;
  onChange: (d: Dest) => void;
  children: ReactNode;
  align?: "start" | "end";
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className="w-[320px] p-0" onKeyDown={(e) => e.stopPropagation()}>
        <DestinationCommand
          value={value}
          onSelect={(d) => {
            onChange(d);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export function DestIcon({ d, className }: { d: Dest; className?: string }) {
  const h = useHierarchy();
  if (d.projectId) return <ProjectGlyph state={h.project(d.projectId)?.state} className={className} />;
  if (d.spaceId) return <SpaceIcon space={h.space(d.spaceId)} className={cn("size-4 text-[9px]", className)} />;
  if (d.areaId) return <Box className={cn("size-3.5", className)} />;
  return <Inbox className={cn("size-3.5", className)} />;
}
